/**
 * ProceduralSpriteFactory — all art is drawn at runtime, no image assets.
 *
 * Phaser 4 "Caladan" ships with a rich filter/shader stack (bloom, glow, noise,
 * displacement). Where available we lean on those via `postFX`. Where they're
 * not — e.g. when the WebGPU renderer is active and APIs differ — we degrade to
 * pure `Graphics` compositing. The *visual language* (neon gradients, thin
 * strokes, SpaceX-blue highlights, red void teeth) is enforced here.
 */

import * as Phaser from 'phaser';
import { GAME } from '@/config/GameConfig';

export type Team = 'friendly' | 'hostile';

export interface UnitSprite {
  container: Phaser.GameObjects.Container;
  team: Team;
  /** Shader-driven Neural Spike color flip. Returns the new team. */
  neuralSpike(): Team;
}

export class ProceduralSpriteFactory {
  constructor(private readonly scene: Phaser.Scene) {}

  // --------------------------------------------------------------------- //
  // STARSHIP — friendly (SpaceX-style slender fuselage, blue neon outline).
  // --------------------------------------------------------------------- //

  starship(x: number, y: number, team: Team = 'friendly'): UnitSprite {
    const base = team === 'friendly' ? GAME.COLOR_FRIENDLY : GAME.COLOR_HOSTILE;
    const hull = this.scene.add.graphics();
    const glow = this.scene.add.graphics();
    const core = this.scene.add.graphics();

    drawStarship(hull, glow, core, base);

    const container = this.scene.add.container(x, y, [glow, hull, core]);
    container.setSize(46, 92);
    tryAddBloom(container, 0.6, base);

    const sprite: UnitSprite = {
      container,
      team,
      neuralSpike: () => {
        sprite.team = sprite.team === 'friendly' ? 'hostile' : 'friendly';
        const nextColor =
          sprite.team === 'friendly' ? GAME.COLOR_FRIENDLY : GAME.COLOR_HOSTILE;
        spikeRipple(this.scene, container.x, container.y, nextColor);
        // Shader-style hue swap: redraw with a tween-driven color lerp.
        tweenRecolor(this.scene, [hull, glow, core], nextColor);
        return sprite.team;
      },
    };
    return sprite;
  }

  // --------------------------------------------------------------------- //
  // ALIEN VOID SEED — jagged, noisy, red-to-violet gradient.
  // --------------------------------------------------------------------- //

  voidSeed(x: number, y: number): UnitSprite {
    const base = GAME.COLOR_HOSTILE;
    const shell = this.scene.add.graphics();
    const spikes = this.scene.add.graphics();
    const iris = this.scene.add.graphics();

    drawVoidSeed(shell, spikes, iris, base);

    const container = this.scene.add.container(x, y, [shell, spikes, iris]);
    container.setSize(64, 64);
    tryAddBloom(container, 0.8, base);

    // Idle breathing scale, lends the noise-shader feel without a true pixel shader.
    this.scene.tweens.add({
      targets: container,
      scale: { from: 0.94, to: 1.06 },
      duration: 1400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const sprite: UnitSprite = {
      container,
      team: 'hostile',
      neuralSpike: () => {
        sprite.team = sprite.team === 'hostile' ? 'friendly' : 'hostile';
        const nextColor =
          sprite.team === 'friendly' ? GAME.COLOR_FRIENDLY : GAME.COLOR_HOSTILE;
        spikeRipple(this.scene, container.x, container.y, nextColor);
        tweenRecolor(this.scene, [shell, spikes, iris], nextColor);
        return sprite.team;
      },
    };
    return sprite;
  }

  // --------------------------------------------------------------------- //
  // COMMAND HUB building — the Terafab.
  // --------------------------------------------------------------------- //

  terafab(x: number, y: number): Phaser.GameObjects.Container {
    const frame = this.scene.add.graphics();
    const lines = this.scene.add.graphics();
    const core = this.scene.add.graphics();

    drawTerafab(frame, lines, core);

    const container = this.scene.add.container(x, y, [frame, lines, core]);
    container.setSize(180, 240);

    // Slow pulsing core — compute tick telegraph.
    this.scene.tweens.add({
      targets: core,
      alpha: { from: 0.6, to: 1.0 },
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    tryAddBloom(container, 0.4, GAME.COLOR_FRIENDLY);
    return container;
  }
}

// ========================================================================= //
// Drawing routines — all procedural, no asset loads.
// ========================================================================= //

function drawStarship(
  hull: Phaser.GameObjects.Graphics,
  glow: Phaser.GameObjects.Graphics,
  core: Phaser.GameObjects.Graphics,
  color: number,
): void {
  // Neon outer glow halo.
  glow.fillStyle(color, 0.18);
  glow.fillEllipse(0, 0, 80, 140);

  // Fuselage — slim, SpaceX Starship silhouette.
  hull.lineStyle(1.5, GAME.COLOR_INK, 0.9);
  hull.fillStyle(0x0a0a0a, 1);
  hull.beginPath();
  hull.moveTo(0, -46);
  hull.lineTo(12, -20);
  hull.lineTo(14, 28);
  hull.lineTo(22, 40);
  hull.lineTo(0, 46);
  hull.lineTo(-22, 40);
  hull.lineTo(-14, 28);
  hull.lineTo(-12, -20);
  hull.closePath();
  hull.fillPath();
  hull.strokePath();

  // Accent stripe in team color.
  core.fillStyle(color, 1);
  core.fillRect(-1.5, -30, 3, 54);
  core.fillStyle(color, 0.9);
  core.fillCircle(0, -12, 3);

  // Exhaust plume dots.
  core.fillStyle(color, 0.7);
  core.fillTriangle(-6, 46, 6, 46, 0, 58);
}

function drawVoidSeed(
  shell: Phaser.GameObjects.Graphics,
  spikes: Phaser.GameObjects.Graphics,
  iris: Phaser.GameObjects.Graphics,
  color: number,
): void {
  // Jagged spike corona — pseudo-noise via deterministic jitter.
  spikes.lineStyle(1, color, 0.7);
  const segments = 18;
  const rNear = 22;
  const rFar = 32;
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    const jitter = 0.6 + 0.4 * Math.sin(i * 2.73);
    const r = rNear + jitter * (rFar - rNear);
    spikes.beginPath();
    spikes.moveTo(Math.cos(a) * rNear * 0.8, Math.sin(a) * rNear * 0.8);
    spikes.lineTo(Math.cos(a) * r, Math.sin(a) * r);
    spikes.strokePath();
  }

  // Obsidian shell.
  shell.fillStyle(0x0a0a0a, 1);
  shell.lineStyle(1.5, color, 0.9);
  shell.fillCircle(0, 0, 20);
  shell.strokeCircle(0, 0, 20);

  // Red iris.
  iris.fillStyle(color, 1);
  iris.fillCircle(0, 0, 7);
  iris.fillStyle(GAME.COLOR_INK, 1);
  iris.fillCircle(-1.5, -1.5, 1.5);
}

function drawTerafab(
  frame: Phaser.GameObjects.Graphics,
  lines: Phaser.GameObjects.Graphics,
  core: Phaser.GameObjects.Graphics,
): void {
  // Gigafactory silhouette — low-slung rectangle with razor borders.
  frame.fillStyle(0x0a0a0a, 1);
  frame.lineStyle(1.5, GAME.COLOR_INK, 0.9);
  frame.fillRect(-80, -60, 160, 120);
  frame.strokeRect(-80, -60, 160, 120);

  // Roof ribbing.
  lines.lineStyle(1, GAME.COLOR_INK, 0.35);
  for (let i = -70; i <= 70; i += 10) {
    lines.beginPath();
    lines.moveTo(i, -60);
    lines.lineTo(i, 60);
    lines.strokePath();
  }
  // Horizontal data-band.
  lines.lineStyle(1, GAME.COLOR_FRIENDLY, 0.9);
  lines.beginPath();
  lines.moveTo(-80, 0);
  lines.lineTo(80, 0);
  lines.strokePath();

  // Pulsing compute core.
  core.fillStyle(GAME.COLOR_FRIENDLY, 1);
  core.fillRect(-6, -6, 12, 12);

  // Antenna/mast.
  lines.lineStyle(1, GAME.COLOR_INK, 0.8);
  lines.beginPath();
  lines.moveTo(0, -60);
  lines.lineTo(0, -96);
  lines.strokePath();
  lines.fillStyle(GAME.COLOR_FRIENDLY, 1);
  lines.fillCircle(0, -98, 2.5);
}

// ========================================================================= //
// Neural Spike — shader-style ripple takeover effect.
// Implemented as concentric expanding rings + bloom on the target. Purely
// stateful Graphics, so renderer-agnostic (WebGL/WebGPU both fine).
// ========================================================================= //

function spikeRipple(scene: Phaser.Scene, x: number, y: number, color: number): void {
  const ring = scene.add.graphics({ x, y });
  ring.lineStyle(3, color, 1);
  ring.strokeCircle(0, 0, 6);

  scene.tweens.add({
    targets: ring,
    scale: { from: 0.1, to: 8 },
    alpha: { from: 1, to: 0 },
    duration: 700,
    ease: 'Cubic.easeOut',
    onComplete: () => ring.destroy(),
  });

  // Inner flash — extra "spike" punch.
  const flash = scene.add.graphics({ x, y });
  flash.fillStyle(color, 0.55);
  flash.fillCircle(0, 0, 20);
  scene.tweens.add({
    targets: flash,
    alpha: 0,
    scale: { from: 0.6, to: 2.6 },
    duration: 420,
    ease: 'Cubic.easeOut',
    onComplete: () => flash.destroy(),
  });
}

function tweenRecolor(
  scene: Phaser.Scene,
  layers: Phaser.GameObjects.Graphics[],
  _target: number,
): void {
  // Graphics fills are baked; we fake a color shift by flashing a translucent
  // overlay in the new team color and briefly tinting via `setAlpha`.
  for (const g of layers) {
    scene.tweens.add({
      targets: g,
      alpha: { from: 0.25, to: 1 },
      duration: 360,
      ease: 'Sine.easeOut',
    });
  }
}

/**
 * Attempt to enable Phaser 4's bloom post-FX on a container. If the API
 * surface differs at runtime (e.g. WebGPU renderer hasn't wired bloom yet)
 * we silently no-op — the procedural gradients still read as neon.
 */
function tryAddBloom(
  obj: Phaser.GameObjects.Container,
  strength: number,
  color: number,
): void {
  // @ts-expect-error postFX is provided by Phaser's component mixin at runtime.
  const fx = obj.postFX ?? obj.preFX;
  try {
    fx?.addBloom?.(color, 1, 1, strength, 1.2);
  } catch {
    /* renderer without bloom FX — ignore */
  }
}
